// SCR-FIGHT (E03-T05), SCR-FIGHT-RESULT и MOD-CONTRIBUTION (E03-T06), MOD-FIGHT-LEAVE и OVL-FIGHT-RECONNECT
// (E03-T07). Раскладка ux/04 §4.1 (обычная и компактная < 600 pt), панель приёмов §4.2, итог §6.
// Числа — только из ответов сервера; сцена PixiJS рисуется в прямоугольнике .fight-scene.
import { signal } from '@preact/signals';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { ANIM_MS, COMBAT_TIMING, type BattleEvent, type BattleResult, type BattleStart, type BattleState, type MoveId } from '@garages/contract';
import { useApp } from '../app/app.js';
import { bossInfo, bossMoveName, itemName, MOVES } from '../content/catalog.js';
import { FightController, type FightView } from '../fight/controller.js';
import { num } from '../i18n/format.js';
import { player, saveCache, setWallet, tutorialStep, wallet } from '../state/game.js';
import { storage } from '../state/storage.js';
import { viewport } from '../telegram/viewport.js';
import { stage, type ScreenProps, type SheetProps } from '../ui/App.js';
import { Dialog, Sheet, useActionBar, useNow } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { LAST_BATTLE_KEY, type LastBattle } from '../boot/boot.js';
import { completeStep, fightTarget, onTutorialTurn, setTut, startTutorial, TEXT } from '../tutorial/flow.js';
import type { FightScene } from '../scene/stage.js';
import { I } from '../assets/icons.js';
import { Icon } from '../assets/ui.js';
import { play as sfx, setSound, soundOn } from '../audio/sfx.js';
import { t as tr } from '../i18n/t.js';
import { Count } from '../ui/text-fx.js';
import { animate, DUR, nudge } from '../ui/motion.js';

export const currentFight = signal<FightController | null>(null);

const OUTCOME_BANNER: Record<BattleResult['outcome'], string> = {
  boss_defeated: tr('UI-FIGHT-FINISHED-ME'), boss_gone: tr('CL-SCREENS-FIGHT-01'), player_defeated: tr('UI-FIGHT-KO'),
  rounds_over: tr('UI-FIGHT-ROUNDS-OVER'), retreat: tr('CL-SCREENS-FIGHT-02'), void: tr('CL-SCREENS-FIGHT-03'),
};

/** Порядок нижнего ряда: «Химия», «Бросок ключа», «Удар» — удар ближе к большому пальцу (ux/04 §4.2). */
const MAIN_ORDER: MoveId[] = ['chem', 'wrench', 'strike'];
const EXTRA_ORDER: MoveId[] = ['kit', 'torch', 'jack', 'weld'];

/** Перезарядка кругом: кольцо убывает по ходам (полное — сколько было при уходе в перезарядку). */
const cdMax = new Map<string, number>();
function CdRing(p: { left: number; moveId: string }) {
  const max = Math.max(p.left, cdMax.get(p.moveId) ?? 0);
  cdMax.set(p.moveId, max);
  const c = 2 * Math.PI * 22;
  return (
    <svg class="cd-ring" viewBox="0 0 50 50" aria-hidden>
      <circle cx="25" cy="25" r="22" fill="none" stroke="rgba(255,255,255,.15)" stroke-width="4" />
      <circle cx="25" cy="25" r="22" fill="none" stroke="var(--accent)" stroke-width="4" stroke-linecap="round" stroke-dasharray={`${c * (p.left / max)} ${c}`} transform="rotate(-90 25 25)" />
    </svg>
  );
}

function MoveButton(p: { m: FightView['moves'][number]; v: FightView; ctrl: FightController; big: boolean; isNew: boolean }) {
  const { m, v, ctrl } = p;
  const app = useApp();
  const state = ctrl.canUse(m);
  const name = MOVES[m.moveId].name;
  const cd = !m.ready && m.cooldown > 0;
  if (!cd) cdMax.delete(m.moveId);
  const noDiesel = m.dieselCost > v.diesel;
  const counter = v.telegraph && (m.moveId === 'jack' || m.moveId === 'kit') && state === 'ok';
  return (
    <button
      class={`move ${p.big ? 'big' : ''} ${state !== 'ok' ? 'off' : ''} ${counter ? 'counter' : ''} ${p.isNew ? 'new' : ''}`}
      data-testid={`move-${m.moveId}`}
      data-tut={`move-${m.moveId}`}
      aria-disabled={state !== 'ok'}
      aria-label={`${name}${cd ? tr('CL-SCREENS-FIGHT-04', { p0: m.cooldown }) : ''}${noDiesel ? tr('CL-SCREENS-FIGHT-05') : ''}`}
      onClick={(e) => {
        if (state === 'diesel') { app.haptic('error'); showToast(tr('CL-SCREENS-FIGHT-06'), 'error'); nudge(e.currentTarget); return; }
        if (state !== 'ok') { nudge(e.currentTarget); return; }
        // Нажатие отдаётся в кнопку: короткий «выстрел» с ореолом, приём уходит в сцену.
        animate(e.currentTarget, [{ transform: 'scale(.9)', boxShadow: '0 0 0 0 rgba(232,145,58,.9)' }, { transform: 'scale(1.06)', boxShadow: '0 0 0 8px rgba(232,145,58,0)', offset: 0.45 }, { transform: 'none', boxShadow: '0 0 0 0 rgba(232,145,58,0)' }], { duration: DUR.slow, fill: 'none' });
        void ctrl.choose(m.moveId);
      }}
    >
      <span class="mv-ico" aria-hidden><I n={m.moveId} size={p.big ? 36 : 28} /></span>
      <span class="mv-name">{name}</span>
      {m.dieselCost ? <span class={`mv-cost ${noDiesel ? 'neg' : ''}`}><I n="diesel" size={12} /> {m.dieselCost}</span> : null}
      {cd ? <span class="mv-cd" aria-hidden><CdRing left={m.cooldown} moveId={m.moveId} /><b key={m.cooldown}>{m.cooldown}</b></span> : null}
      {p.isNew ? <span class="mv-new">{tr('UI-SKILL-NEW')}</span> : null}
    </button>
  );
}

function TimerBar(p: { endsAt: number | null }) {
  const now = useNow(100);
  if (!p.endsAt) return <div class="fight-timer" aria-hidden />;
  const left = Math.max(0, p.endsAt - now);
  const frac = Math.min(1, left / COMBAT_TIMING.choiceTimerMs);
  // Полоса таймера — масштабом (transform), не шириной: без перерасчёта раскладки 10 раз в секунду.
  return (
    <div class="fight-timer" role="timer" aria-label={tr('CL-SCREENS-FIGHT-07', { p0: Math.ceil(left / 1000) })}>
      <i class={left < 2000 ? 'low' : ''} style={{ transform: `scaleX(${frac})` }} />
    </div>
  );
}

/**
 * Полоса HP с «хвостом»: заливка падает сразу, светлый хвост догоняет через 0.35 с — видно, сколько сняли
 * этим ударом; полоса вздрагивает и вспыхивает. Только transform и opacity.
 */
function HpBar(p: { hp: number; max: number; label: string; mine?: number; testId: string; compact?: boolean }) {
  const frac = p.max ? Math.max(0, Math.min(1, p.hp / p.max)) : 0;
  const ref = useRef<HTMLDivElement>(null);
  const prev = useRef(frac);
  const down = frac < prev.current;
  useLayoutEffect(() => {
    if (frac < prev.current - 0.001) {
      nudge(ref.current);
      animate(ref.current?.querySelector('.hp-flash'), [{ opacity: 0.55 }, { opacity: 0 }], { duration: DUR.slow, fill: 'none' });
    }
    prev.current = frac;
  }, [frac]);
  return (
    <div class="hp" ref={ref} data-testid={p.testId} role="meter" aria-valuenow={p.hp} aria-valuemax={p.max} aria-label={p.label}>
      <i class="hp-trail" style={{ transform: `scaleX(${frac})`, transitionDelay: down ? '350ms' : '0ms' }} />
      <i class="hp-fill" style={{ transform: `scaleX(${frac})` }} />
      <i class="hp-flash" />
      <span>{p.label} {num(p.hp)} / {num(p.max)}</span>
    </div>
  );
}

function Reconnect(p: { until: number }) {
  const now = useNow(250);
  const s = Math.max(0, Math.ceil((p.until - now) / 1000));
  return (
    <div class="overlay center" style={{ zIndex: 15 }} data-testid="OVL-FIGHT-RECONNECT" role="alert">
      <div class="dialog" style={{ textAlign: 'center' }}>
        <h2>{tr('CL-SCREENS-FIGHT-08')}</h2>
        <p>{s > 0 ? tr('CL-SCREENS-FIGHT-09', { p0: s }) : tr('CL-SCREENS-FIGHT-10')}</p>
        <div class="spinner" style={{ margin: '0 auto', width: 28, height: 28 }} />
      </div>
    </div>
  );
}

export function FightScreen(p: ScreenProps) {
  const app = useApp();
  const tutorial = !!p.params['tutorial'];
  const init = (p.params['start'] ?? p.params['state']) as BattleStart | BattleState;
  const restored = !!p.params['restored'];
  const [v, setV] = useState<FightView | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  /** HP, уменьшаемые по ходу анимации — до прихода итогового состояния хода. */
  const [live, setLive] = useState<{ boss: number; player: number } | null>(null);
  const [sound, setSoundState] = useState(soundOn());
  const sceneRef = useRef<HTMLDivElement>(null);
  /** Чей ход — подпись над сценой сменяется переходом (ux/04 §4.1). */
  const [turn, setTurn] = useState<'you' | 'boss' | null>(null);
  const fsRef = useRef<FightScene | null>(null);
  const ctrlRef = useRef<FightController | null>(null);
  const compact = viewport.value.height !== 'normal';
  useActionBar({ hidden: true });

  useEffect(() => {
    const ctrl = new FightController({
      call: app.call,
      noTimer: tutorial,
      introMs: restored ? 0 : 2000,
      onChange: (x) => setV(x),
      onHaptic: (k) => { app.haptic(k); if (k === 'success') sfx('win'); },
      play: async (events, view) => {
        let boss = view.boss.hp;
        let pl = view.player.hp;
        // Отклик по событию (ux/04 §4.4, §4.8): вибрация, звук и полоса HP — в момент удара, а не пачкой.
        const onEvent = (e: BattleEvent) => {
          if (e.e === 'hit_boss' && !e.miss) {
            boss = Math.max(0, boss - e.effective);
            app.haptic(e.crit ? 'heavy' : 'medium');
            sfx(e.crit ? 'crit' : 'hit');
          } else if (e.e === 'hit_player' && !e.dodged) {
            pl = Math.max(0, pl - e.dmg);
            app.haptic('soft');
          } else if (e.e === 'effect_tick') {
            if (e.target === 'boss') boss = Math.max(0, boss - (e.effective ?? e.dmg)); else pl = Math.max(0, pl - e.dmg);
          } else if (e.e === 'heal') {
            if (e.target === 'player') pl += e.amount; else boss += e.amount;
          } else if (e.e === 'telegraph') app.haptic('warning');
          else if (e.e === 'boss_move') setTurn('boss');
          setLive({ boss, player: pl });
        };
        const fs = fsRef.current;
        if (fs) await fs.play(events, onEvent);
        else for (const e of events) { onEvent(e); await new Promise((r) => setTimeout(r, ANIM_MS[e.e])); }
        setLive(null);
      },
    }, init, { restored });
    ctrlRef.current = ctrl;
    currentFight.value = ctrl;
    storage.setJson(LAST_BATTLE_KEY, { battleId: init.battleId, seen: false, origin: init.boss.bossId } satisfies LastBattle);
    const lastDeadline = 'lastTurn' in init && init.lastTurn ? init.lastTurn.state.turnDeadlineAt : init.turnDeadlineAt;
    void ctrl.begin(lastDeadline, restored ? [] : init.openingEvents);
    // Back → MOD-FIGHT-LEAVE (ux/02 §3); в обучении T03–T04 BackButton скрыта.
    app.nav.intercept(p.entryKey, () => { if (!tutorial) app.nav.openSheet('MOD-FIGHT-LEAVE'); return true; });
    const vis = () => { if (document.hidden) ctrl.onHidden(); else void ctrl.onResume(); };
    document.addEventListener('visibilitychange', vis);
    app.wa?.onEvent?.('activated', vis);
    return () => {
      ctrl.dispose();
      currentFight.value = null;
      document.removeEventListener('visibilitychange', vis);
      app.wa?.offEvent?.('activated', vis);
      app.nav.intercept(p.entryKey, null);
    };
  }, []);

  // Сцена Pixi в прямоугольнике .fight-scene; пересчёт при смене размера окна.
  useLayoutEffect(() => {
    const el = sceneRef.current;
    const st = stage.value;
    if (!el || !st) return;
    const rect = () => { const b = el.getBoundingClientRect(); return { x: b.x, y: b.y, w: b.width, h: b.height }; };
    void st.show('fight', { bossId: init.boss.bossId, rect: rect() }).then((fs) => { fsRef.current = fs; });
    const ro = new ResizeObserver(() => st.setRect(rect()));
    ro.observe(el);
    return () => { ro.disconnect(); fsRef.current = null; };
  }, [stage.value, !!v]); // .fight-scene появляется только с первым состоянием контроллера

  // Ход игрока: герой собирается в стойку, камера сдвигается к нему; подпись «Твой ход».
  useEffect(() => {
    if (!v) return;
    const fs = fsRef.current;
    if (v.phase === 'ready') { fs?.setTurn('player'); setTurn('you'); }
    else if (v.phase === 'over' || v.phase === 'intro') { fs?.setTurn('idle'); setTurn(null); }
    else fs?.setTurn('idle');
  }, [v?.phase, v?.turnNo, !!fsRef.current]);

  // Обучение: подсветка нужного приёма и шаги T03/T04 (ГД 12 §3).
  useEffect(() => {
    if (!tutorial || !v) return;
    if (v.phase === 'ready') {
      const t = fightTarget(v.moves, v.turnNo, v.round);
      setTut({ target: t.target, say: t.say, hint: t.hint });
      void onTutorialTurn(app, v.turnNo, false);
    } else setTut({ target: null, say: null, hint: null });
  }, [v?.phase, v?.turnNo]);

  // Конец захода: баннер исхода 1 с, затем итог замещает бой (ux/04 §4.5).
  useEffect(() => {
    if (v?.phase !== 'over' || !v.result) return;
    app.nav.closeConfirm();
    fsRef.current?.finale(v.result.outcome);
    setBanner(tutorial && v.result.outcome === 'boss_defeated' ? tr('TUT-T04-08') : OUTCOME_BANNER[v.result.outcome]);
    const r = v.result;
    const t = setTimeout(async () => {
      if (tutorial) {
        if (tutorialStep.value === 'T03') await completeStep(app, 'T03');
        if (tutorialStep.value === 'T04') await completeStep(app, 'T04');
        startTutorial(app, tutorialStep.value);
        return;
      }
      app.nav.replace('SCR-FIGHT-RESULT', { result: r, bossId: init.boss.bossId });
    }, 1000);
    return () => clearTimeout(t);
  }, [v?.phase]);

  if (!v) return <div class="content screen-bg" data-testid="SCR-FIGHT" />;
  const ctrl = ctrlRef.current!;
  const info = bossInfo(v.bossId);
  const has = new Map(v.moves.map((m) => [m.moveId, m]));
  const mainRow = MAIN_ORDER.filter((id) => has.has(id)).map((id) => has.get(id)!);
  const extraRow = EXTRA_ORDER.filter((id) => has.has(id)).map((id) => has.get(id)!);
  const lefty = storage.get('hand') === 'left';
  if (lefty) mainRow.reverse();
  const logLines = logOpen ? v.log.slice(-20) : v.log.slice(compact ? -1 : -3);
  return (
    <div class={`fight ${compact ? 'compact' : ''}`} data-testid="SCR-FIGHT" data-phase={v.phase}>
      <div class="fight-top">
        <span data-testid="fight-round">{tr('CL-SCREENS-FIGHT-11')}{" "}<span class="round-num" key={v.round}>{v.round}</span> / {COMBAT_TIMING.maxRounds}</span>
        <b class="fight-boss-name">{info.short}</b>
        <button class="sound-btn" data-testid="fight-sound" aria-label={sound ? tr('CL-SCREENS-FIGHT-12') : tr('CL-SCREENS-FIGHT-13')} aria-pressed={sound} onClick={() => { setSound(!sound); setSoundState(!sound); }}><I n={sound ? 'sound' : 'mute'} size={22} /></button>
      </div>
      <div class="fight-hp-boss"><HpBar hp={live?.boss ?? v.boss.hp} max={v.boss.hpMax} label={info.hpLabel} testId="boss-hp" /></div>
      <div class="fight-scene" ref={sceneRef}>
        {v.telegraph ? (
          <div class="telegraph" role="alert" aria-live="assertive" data-testid="telegraph">
            <b><I n="warn" size={18} /> {info.short} {" "}{tr('CL-SCREENS-FIGHT-14')}{" "}{bossMoveName(v.telegraph.moveId)}…</b>
            <span>{tr('CL-SCREENS-FIGHT-15')}{" "}{v.moves.some((m) => m.moveId === 'jack' || m.moveId === 'kit') ? tr('CL-SCREENS-FIGHT-16') : tr('CL-SCREENS-FIGHT-17')}</span>
          </div>
        ) : null}
        {v.phase === 'intro' ? <div class="fight-intro" data-testid="fight-intro"><span>{tr('UI-FIGHT-START')}</span></div> : null}
        {v.phase === 'intro' ? <div class="boss-intro" aria-hidden><b>{info.name}</b></div> : null}
        {turn && v.phase !== 'over' ? <div class={`turn-badge ${turn}`} key={`${turn}-${v.turnNo}`} aria-live="polite">{tr(turn === 'you' ? 'CL-FIGHT-TURN-YOU' : 'CL-FIGHT-TURN-BOSS')}</div> : null}
        {v.slow ? <div class="fight-slow" aria-label={tr('CL-SCREENS-FIGHT-18')}>…</div> : null}
        {banner ? <div class={`fight-intro fight-banner ${v.result?.outcome === 'boss_defeated' ? 'win' : v.result?.outcome === 'player_defeated' ? 'lose' : ''}`} data-testid="fight-banner"><span>{banner}</span></div> : null}
      </div>
      <div class="fight-player">
        <HpBar hp={Math.min(live?.player ?? v.player.hp, v.player.hpMax)} max={v.player.hpMax} label={tr('CL-SCREENS-FIGHT-19')} testId="player-hp" />
        <span class="diesel" aria-label={tr('CL-SCREENS-FIGHT-20', { p0: v.diesel })}><I n="diesel" size={18} /> {v.diesel}</span>
      </div>
      <button class={`fight-log ${logOpen ? 'open' : ''}`} data-testid="fight-log" onClick={() => setLogOpen(!logOpen)} aria-expanded={logOpen}>
        {logLines.length ? logLines.map((l, i) => <div key={i}>{l}</div>) : <div class="muted">{tutorial ? '' : tr('CL-SCREENS-FIGHT-21')}</div>}
      </button>
      {v.autoStreak >= 3 ? <div class="small warn" style={{ textAlign: 'center' }}>{tr('CL-SCREENS-FIGHT-22')}</div> : null}
      <TimerBar endsAt={v.phase === 'ready' ? v.choiceEndsAt : null} />
      <div class={`moves ${v.phase !== 'ready' ? 'busy' : ''}`} data-testid="moves">
        {extraRow.length ? <div class="moves-row extra">{extraRow.map((m) => <MoveButton key={m.moveId} m={m} v={v} ctrl={ctrl} big={false} isNew={v.newMoves.includes(m.moveId)} />)}</div> : null}
        <div class="moves-row main">{mainRow.map((m) => <MoveButton key={m.moveId} m={m} v={v} ctrl={ctrl} big isNew={v.newMoves.includes(m.moveId)} />)}</div>
      </div>
      {v.phase === 'reconnect' && v.reconnectUntil ? <Reconnect until={v.reconnectUntil} /> : null}
    </div>
  );
}

/** MOD-FIGHT-LEAVE ⛔ (ux/04 §5): таймер хода на фоне идёт — в окне виден раунд и полоса таймера. */
export function FightLeaveDialog(_p: SheetProps) {
  const app = useApp();
  const ctrl = currentFight.value;
  const [v, setV] = useState(ctrl?.v ?? null);
  useEffect(() => { const t = setInterval(() => setV(ctrl ? { ...ctrl.v } : null), 200); return () => clearInterval(t); }, []);
  const shared = v && v.mode !== 'solo';
  return (
    <Dialog
      title={tr('CL-SCREENS-FIGHT-23')}
      testId="MOD-FIGHT-LEAVE"
      text={shared ? tr('CL-SCREENS-FIGHT-24') : tr('CL-SCREENS-FIGHT-25')}
      actions={[
        { text: tr('CL-SCREENS-FIGHT-26'), tone: 'danger', testId: 'leave-go', onClick: async () => { await ctrl?.retreat(); app.nav.closeConfirm(); } },
        { text: tr('UI-FIGHT-LEAVE-STAY'), testId: 'leave-stay', onClick: () => app.nav.closeConfirm() },
      ]}
    >
      {v ? <p class="small">{tr('CL-SCREENS-FIGHT-11')}{" "}{v.round} / 10</p> : null}
      {v ? <TimerBar endsAt={v.phase === 'ready' ? v.choiceEndsAt : null} /> : null}
    </Dialog>
  );
}

const RESULT_TITLE: Record<BattleResult['outcome'], string> = {
  boss_defeated: tr('UI-FIGHT-WIN'), boss_gone: tr('CL-SCREENS-FIGHT-27'), player_defeated: tr('CL-SCREENS-FIGHT-28'),
  rounds_over: tr('UI-FIGHT-ROUNDS-OVER'), retreat: tr('CL-SCREENS-FIGHT-02'), void: tr('CL-SCREENS-FIGHT-29'),
};

/**
 * SCR-FIGHT-RESULT (ux/04 §6): каждый исход — свой вид. Кнопки активны через 2.5 с. Обрыв на итоге →
 * при возвращении итог показывается один раз (флаг seen в localStorage, E03-T06).
 * В обучении (T05) — только «Забрать», награда начислена сервером в момент победы (ГД 12 §5).
 */
export function FightResultScreen(p: ScreenProps) {
  const app = useApp();
  const tutorial = !!p.params['tutorial'];
  const r = p.params['result'] as BattleResult | undefined;
  const bossId = String(p.params['bossId'] ?? 'B01');
  const [armed, setArmed] = useState(false);
  const [claimed, setClaimed] = useState<{ nuts: number; item: string | null } | null>(null);
  useEffect(() => { void stage.value?.show('none'); }, [stage.value]);
  useEffect(() => {
    const t = setTimeout(() => setArmed(true), 2500);
    if (r) {
      const lb = storage.getJson<LastBattle>(LAST_BATTLE_KEY);
      if (lb && lb.battleId === r.battleId) storage.setJson(LAST_BATTLE_KEY, { ...lb, seen: true });
      app.haptic(r.outcome === 'boss_defeated' || r.outcome === 'boss_gone' ? 'success' : 'medium');
      app.call('getWallet').then(setWallet).catch(() => {});
      app.call('getMe').then((pl) => { player.value = pl; saveCache(); }).catch(() => {}); // уровень и опыт после захода
    }
    app.nav.intercept(p.entryKey, () => { app.nav.pop(); return true; }); // Back → экран, с которого начат заход
    return () => { clearTimeout(t); app.nav.intercept(p.entryKey, null); };
  }, []);
  // Новый уровень → MOD-LEVEL-UP после итога (ux/04 §6.1).
  useEffect(() => { if (armed && r?.levelUp) app.nav.openSheet('MOD-LEVEL-UP', r.levelUp); }, [armed]);
  const w = wallet.value;
  const cost = COMBAT_TIMING.entryEnergy;
  const enough = (w?.energy.value ?? 0) >= cost;
  useActionBar(tutorial ? {
    main: {
      text: tr('UI-BTN-COLLECT'), testId: 'claim', disabled: !armed && false,
      onClick: async () => {
        const res = await completeStep(app, 'T05', 'claim');
        const nuts = res.rewards?.currencies?.nuts ?? 0;
        setClaimed({ nuts, item: res.rewards?.items?.[0]?.defId ?? null });
        app.haptic('success');
        setTut({ target: null, say: TEXT.T05level, hint: null });
        await new Promise((x) => setTimeout(x, 1500));
        startTutorial(app, res.step);
      },
    },
  } : {
    secondary: { text: tr('CL-SCREENS-FIGHT-30'), tone: 'outline', testId: 'to-map', disabled: !armed, onClick: () => app.nav.pop() },
    main: enough
      ? { text: tr('CL-SCREENS-FIGHT-31', { p0: cost }), icon: 'energy' as const, testId: 'again', disabled: !armed, onClick: () => { app.nav.pop(); app.nav.openSheet('MOD-BOSS', { bossId }); } }
      : { text: tr('CL-SCREENS-FIGHT-32'), testId: 'refill', disabled: !armed, onClick: () => app.nav.openSheet('MOD-ENERGY', { need: cost }) },
  });
  if (tutorial) {
    return (
      <div class="content screen-bg" data-testid="SCR-FIGHT-RESULT" data-outcome="tutorial">
        <div class="page col" style={{ alignItems: 'center', textAlign: 'center', paddingTop: 'var(--safe-top)' }}>
          <h1 style={{ margin: '8px 0 0' }}>{tr('TUT-T05-01')}</h1>
          <div class="card col" style={{ width: '100%' }}>
            <div class="row"><I n="nuts" size={28} /><span>{tr('TUT-T05-02')}{claimed ? ` · +${claimed.nuts}` : ''}</span></div>
            <div class="row"><I n="head" size={28} /><span>{tr('TUT-T05-03')}</span></div>
            <div class="row"><I n="xp" size={28} /><span>{tr('TUT-T05-04')}</span></div>
          </div>
        </div>
      </div>
    );
  }
  if (!r) return <div class="content screen-bg" data-testid="SCR-FIGHT-RESULT"><div class="page col"><div class="skeleton" style={{ height: 40 }} /><p class="muted">{tr('CL-SCREENS-FIGHT-33')}</p></div></div>;
  const info = bossInfo(bossId);
  const win = r.outcome === 'boss_defeated';
  const me = player.value;
  return (
    <div class="content screen-bg" data-testid="SCR-FIGHT-RESULT" data-outcome={r.outcome}>
      <div class="page col result-page">
        <div class="result-fig" aria-hidden style={win ? undefined : { filter: 'grayscale(.6) brightness(.75)' }}><Icon id={`FIG-${bossId.replace(/-T$/, '')}`} fallback="" size={200} /></div>
        <div class="result-plate col">
          <div class="row" style={{ gap: 10 }}>
            <I n={win ? 'win' : r.outcome === 'player_defeated' ? 'ko' : r.outcome === 'retreat' ? 'retreat' : 'timeout'} size={40} />
            <div class="col" style={{ gap: 0, flex: 1, minWidth: 0 }}>
              <h1 data-testid="result-title">{RESULT_TITLE[r.outcome]}</h1>
              <span class="rp-sub">{win ? info.win : info.name}</span>
            </div>
            <div class="col rp-dmg">
              <b data-testid="result-damage"><Count value={r.damage.effective} from={0} delay={250} duration={900} bump={false} /></b>
              <span>{tr('UI-RESULT-DAMAGE')} · {tr('CL-SCREENS-FIGHT-34')}{" "}{r.rounds}</span>
            </div>
          </div>
          {r.contribution ? (
            <button class="rp-line" onClick={() => app.nav.openSheet('MOD-CONTRIBUTION', { contribution: r.contribution })}>
              <span style={{ flex: 1 }}>{tr('CL-SCREENS-FIGHT-35')}{" "}{num(r.contribution.damage)} {" "}{tr('CL-SCREENS-FIGHT-36')}{" "}{r.contribution.rank}</span><span class="accent-text">{tr('CL-SCREENS-FIGHT-37')}</span>
            </button>
          ) : (
            <span class="rp-line">{win ? tr('CL-SCREENS-FIGHT-38') : r.outcome === 'retreat' ? tr('CL-SCREENS-FIGHT-39') : tr('CL-SCREENS-FIGHT-40')}</span>
          )}
          <div class="row rp-rewards" data-testid="result-rewards">
            {r.rewards.xp ? <span class="badge reward-pop" style={{ '--i': 0 }}><I n="xp" size={20} /> +<Count value={r.rewards.xp} from={0} delay={300} bump={false} /> {" "}{tr('CL-SCREENS-FIGHT-42')}</span> : null}
            {r.rewards.nuts ? <span class="badge reward-pop" style={{ '--i': 1 }}><I n="nuts" size={20} /> +<Count value={r.rewards.nuts} from={0} delay={370} bump={false} /></span> : null}
            {r.rewards.scrap ? <span class="badge reward-pop" style={{ '--i': 2 }}><I n="scrap" size={20} /> +<Count value={r.rewards.scrap} from={0} delay={440} bump={false} /></span> : null}
            {r.rewards.items.map((it, i) => (
              <button key={it.itemId} class={`badge reward-pop rarity-${it.rarity}`} style={{ border: '2px solid var(--r)', minHeight: 32, '--i': 3 + i }} onClick={() => app.nav.openSheet('MOD-ITEM', { itemId: it.itemId, fromResult: true })}>
                {itemName(it.defId)}
              </button>
            ))}
            {!r.rewards.xp && !r.rewards.nuts && !r.rewards.items.length ? <span class="rp-sub">{tr('CL-SCREENS-FIGHT-43')}</span> : null}
          </div>
          {me && me.xpToNext ? <div class="progress" aria-label={tr('CL-SCREENS-FIGHT-44')}><i style={{ width: `${Math.min(100, (me.xp / me.xpToNext) * 100)}%` }} /></div> : null}
          {r.energyRefund ? <span class="rp-line pos">+{r.energyRefund} <I n="energy" size={14} /> {" "}{tr('CL-SCREENS-FIGHT-45')}</span> : null}
          {w ? <span class="rp-sub" style={{ textAlign: 'center' }}>{tr('CL-SCREENS-FIGHT-46')}{" "}{w.energy.value} <I n="energy" size={14} /> {" "}{tr('CL-SCREENS-FIGHT-47')}{" "}{Math.floor(w.energy.value / cost)} {Math.floor(w.energy.value / cost) === 1 ? tr('CL-SCREENS-FIGHT-48') : tr('CL-SCREENS-FIGHT-49')}</span> : null}
        </div>
      </div>
    </div>
  );
}

/** MOD-CONTRIBUTION — этап А; в П показывается только своя строка из BattleResult.contribution. */
export function ContributionSheet({ entry }: SheetProps) {
  const app = useApp();
  const c = entry.params['contribution'] as BattleResult['contribution'];
  return (
    <Sheet entry={entry} title={tr('CL-SCREENS-FIGHT-50')} actions={[{ text: tr('CL-SCREENS-FIGHT-51'), onClick: () => app.nav.popSheet() }]}>
      {c ? (
        <div class="list">
          <div class="list-row"><b>{c.rank}</b><span style={{ flex: 1 }}>{tr('CL-SCREENS-FIGHT-19')}</span><span>{num(c.damage)}</span><span>{c.qualified ? '✓' : ''}</span></div>
          <p class="small muted">{tr('CL-SCREENS-FIGHT-52')}</p>
        </div>
      ) : <div class="empty">{tr('CL-SCREENS-FIGHT-53')}</div>}
    </Sheet>
  );
}
